import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Typography, Paper, CircularProgress, Alert, TextField, Button,
    Table, TableHead, TableRow, TableCell, TableBody, Tooltip,
    Grid,
    Stack,
    InputAdornment
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PeopleIcon from '@mui/icons-material/People';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import PercentIcon from '@mui/icons-material/Percent';
import { InfluencerStorefrontService } from '../../../../core/services/influencer-storefront.service';
import { Add, InfoOutlined, Inventory2Outlined, SearchOutlined } from '@mui/icons-material';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
} from 'recharts';
import RechartsNumberTooltip from '../../../../components/tailwind-component/ui/rechartsNumberTooltip';

interface Props { influencer_id: number; account_id: number; }

// const StatCard = ({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: any; color: string }) => (
//     <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1, minWidth: 160 }}>
//         <Box display="flex" alignItems="center" gap={1} mb={0.5} sx={{ color }}>{icon}
//             <Typography variant="caption" color="text.secondary">{label}</Typography>
//         </Box>
//         <Typography variant="h5" fontWeight={700}>{value}</Typography>
//     </Paper>
// );

const StatCard = ({
    icon,
    label,
    value,
    color,
}: any) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            borderRadius: '16px',
            bgcolor: "#fff",
            border: "1px solid #E5E7EB",
            transition: ".25s",

            "&:hover": {
                transform: "translateY(-4px)",
                boxShadow:
                    "0 20px 40px rgba(15,23,42,.08)",
            },
        }}
    >
        <Box
            display="flex"
            justifyContent="space-between"
        >
            <Box>
                <Typography
                    color="text.secondary"
                    fontSize={13}
                >
                    {label}
                </Typography>

                <Typography
                    fontSize={34}
                    fontWeight={800}
                    mt={1}
                >
                    {value}
                </Typography>
            </Box>

            <Box
                sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    bgcolor: `${color}15`,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color,
                }}
            >
                {icon}
            </Box>
        </Box>
    </Paper>
);

export const InfluencerStorefrontAnalytics: React.FC<Props> = ({ influencer_id, account_id }) => {
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState<any>({});
    const [perProduct, setPerProduct] = useState<any[]>([]);
    const [trend, setTrend] = useState<any[]>([]);
    // Draft values bound to the two date inputs. Editing these must NOT refetch —
    // OLD behaviour: `load` depended on them and `useEffect(..., [load])` re-ran on
    // every keystroke/pick, so the filter auto-applied before "Apply Filters".
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    // Committed values — only these drive the query, and only "Apply Filters" moves
    // the drafts into them.
    const [appliedStart, setAppliedStart] = useState('');
    const [appliedEnd, setAppliedEnd] = useState('');
    const [alert, setAlert] = useState<string | null>(null);

    const today = new Date().toLocaleDateString('en-CA');

    const load = useCallback(() => {
        if (!influencer_id || !account_id) return;
        setAlert(null);
        if (startDate && startDate > today) {
            setAlert('From date cannot be in the future');
            return;
        }
        if (endDate && endDate > today) {
            setAlert('To date cannot be in the future');
            return;
        }
        if (startDate && endDate && startDate > endDate) {
            setAlert('From date cannot be after To date');
            return;
        }
        setLoading(true);
        InfluencerStorefrontService.getAnalytics({ influencer_id, account_id, start_date: appliedStart, end_date: appliedEnd })
            .then((res: any) => {
                if (res?.status === 1 && res?.data?.rows) {
                    setSummary(res.data.rows.summary || {});
                    setPerProduct(res.data.rows.per_product || []);
                    setTrend(res.data.rows.trend || []);
                } else {
                    setSummary({}); setPerProduct([]); setTrend([]);
                }
            })
            .catch(() => setAlert('Failed to load analytics'))
            .finally(() => setLoading(false));
    }, [influencer_id, account_id, startDate, endDate, today]);

    useEffect(() => { load(); }, [load]);

    // A Affiliate Deals cross-post's product_url is the raw Trackier tracking_url
    // (needed server-side to resolve /storefront/r/:click_token) — never surface
    // that here, same as the Storefront product list and Affiliate Deals itself.
    const displayLink = (p: any) =>
        p.source_type === 'cashkaro' && p.click_token ? InfluencerStorefrontService.clickUrl(p.click_token) : p.product_url;

    // Today, as YYYY-MM-DD, for the inputs' `max` so future dates can't be picked.
    const todayStr = new Date().toLocaleDateString('en-CA');

    // Either bound may be used on its own:
    //   From only -> that date onwards;  To only -> everything up to that date.
    // The only invalid combination is an inverted range.
    const dateError =
        startDate && endDate && startDate > endDate ? '"From" cannot be after "To".'
        : (startDate > todayStr || endDate > todayStr) ? 'Future dates are not allowed.'
        : '';

    // Commits the drafts; `load` then refetches because appliedStart/appliedEnd changed.
    const applyFilters = () => {
        if (dateError) return;
        setAppliedStart(startDate);
        setAppliedEnd(endDate);
    };

    const filtersActive = !!startDate || !!endDate || !!appliedStart || !!appliedEnd;

    const clearFilters = () => {
        setStartDate(''); setEndDate('');
        setAppliedStart(''); setAppliedEnd('');
    };

    const chartData = trend.map((item) => ({
        date: item.date,
        Views: Number(item.views || 0),
        Visitors: Number(item.visitors || 0),
    }));

    return (
        <Box>
            <Grid
    container
    spacing={3}
    alignItems="stretch"
    sx={{ mb: 4 }}
>
    <Grid item xs={12}>
<Box
    sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", lg: "center" },
        flexDirection: { xs: "column", lg: "row" },
        gap: 3,
        mb: 1,
    }}
>
    {/* Left */}
    <Box>
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
        >
            <Typography
                variant="h5"
                fontWeight={700}
            >
                Store Analytics
            </Typography>

            <Tooltip title="Monitor your storefront performance, visitor activity and product engagement.">
                <InfoOutlined
                    sx={{
                        fontSize: 18,
                        color: "text.secondary",
                        cursor: "pointer",
                    }}
                />
            </Tooltip>
        </Stack>

        <Typography
            color="text.secondary"
        >
            Monitor traffic, visitors and product engagement across your storefront.
        </Typography>
    </Box>

    {/* Right */}
    <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ width: { xs: "100%", sm: "auto" } }}
    >
        <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            inputProps={{ max: endDate && endDate < todayStr ? endDate : todayStr }}
            error={!!dateError}
            sx={{ flex: { xs: 1, sm: "none" }, minWidth: 120 }}
        />

        <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            inputProps={{ min: startDate || undefined, max: todayStr }}
            error={!!dateError}
            sx={{ flex: { xs: 1, sm: "none" }, minWidth: 120 }}
        />

        <Button
            variant="contained"
            onClick={applyFilters}
            disabled={!!dateError}
            sx={{
                bgcolor: "#000",
                borderRadius: "10px",
                px: 3,
                height: 40,
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                "&:hover": {
                    bgcolor: "#111",
                },
            }}
        >
            Apply Filters
        </Button>

        {filtersActive && (
            <Button
                variant="text"
                onClick={clearFilters}
                sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", color: "#6B7280", height: 40 }}
            >
                Clear
            </Button>
        )}

        {dateError && (
            <Typography variant="caption" color="error" sx={{ fontWeight: 500, width: "100%" }}>
                {dateError}
            </Typography>
        )}
    </Stack>
</Box>
 

</Grid></Grid>
            {/* <Grid
    container
    spacing={3}
    alignItems="stretch"
    sx={{ mb: 4 }}
>

<Grid item xs={12} sm={6} md={3}>
                
                <Paper
    sx={{
        p:4,
        borderRadius:4,
        mb:4,
        background:
        "linear-gradient(135deg,#2563EB,#7C3AED)",
        color:"white"
    }}
>

<Typography
variant="h4"
fontWeight={800}
>
Store Analytics
</Typography>

<Typography
sx={{
opacity:.85,
mt:1
}}
>
Monitor visitors, clicks and storefront performance.
</Typography>

</Paper>
                
                <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                    value={startDate} onChange={e => setStartDate(e.target.value)} />
                <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                    value={endDate} onChange={e => setEndDate(e.target.value)} />
                <Button variant="outlined" onClick={load}>Apply</Button>
            </Grid>
            </Grid>

            <Typography variant="body2" color="text.secondary" mb={2}>
                Sales can’t be tracked for external affiliate links, but here’s how your store is performing.
            </Typography> */}

            {alert && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAlert(null)}>{alert}</Alert>}

            {loading ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : (
                <>
                    <Grid
    container
    spacing={3}
    mb={4}
>
                        <Grid item xs={6} sm={6} md={3}><StatCard icon={<VisibilityIcon fontSize="small" />} label="Store views" value={summary.total_views ?? 0} color="#2563EB" /></Grid>
                        <Grid item xs={6} sm={6} md={3}><StatCard icon={<PeopleIcon fontSize="small" />} label="Unique visitors" value={summary.unique_visitors ?? 0} color="#7C3AED" /></Grid>
                        <Grid item xs={6} sm={6} md={3}><StatCard icon={<TouchAppIcon fontSize="small" />} label="Product clicks" value={summary.total_clicks ?? 0} color="#16A34A" /></Grid>
                        <Grid item xs={6} sm={6} md={3}><StatCard icon={<PercentIcon fontSize="small" />} label="Click-through rate" value={`${summary.ctr ?? 0}%`} color="#D97706" /></Grid>
                    </Grid>

                    {/* Simple views trend (last 30 days / selected range) */}
                    <Paper
    elevation={0}
    sx={{
        p:{ xs: 2, md: 4 },
        borderRadius:'16px',
        border:"1px solid #E5E7EB",
        mb:4
    }}
>
                        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Views trend</Typography>
                        {chartData.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No views in this period yet.</Typography>
                        ) : (
                            <Box sx={{ height: 320, width: '100%' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={chartData}>
                                        <defs>
                                            <linearGradient id="storeViewsGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#2563EB" stopOpacity={0.4} />
                                                <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="4 4" vertical={false} />
                                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                        <YAxis />
                                        <RechartsNumberTooltip />
                                        <Area type="monotone" dataKey="Views" stroke="#2563EB" strokeWidth={3} fill="url(#storeViewsGradient)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </Box>
                        )}
                    </Paper>

                    {/* Per-product clicks */}
                    <Typography variant="subtitle2" fontWeight={700} mb={1}>Top products by clicks</Typography>
                    <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                        {perProduct.length === 0 ? (
                            <Box textAlign="center" py={8}>
                                <Inventory2Outlined sx={{ fontSize: 70, color: "#CBD5E1" }} />
                                <Typography mt={2} fontWeight={700}>No products added yet</Typography>
                                <Typography color="text.secondary" mt={1}>
                                    Products with affiliate links will appear here.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {/* Desktop / tablet: table (horizontally scrollable on tablet) */}
                                <Box sx={{ display: { xs: 'none', sm: 'block' }, overflowX: 'auto' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ background: '#F8FAFC' }}>
                                                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Product</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Link</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#475569' }} align="right">Clicks</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {perProduct.map((p) => (
                                                <TableRow key={p.product_id} hover>
                                                    <TableCell>
                                                        <Typography variant="body2" noWrap sx={{ maxWidth: 320 }} title={p.product_title}>{p.product_title}</Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ maxWidth: 280 }}>
                                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }} title={displayLink(p)}>{displayLink(p)}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right"><Typography variant="body2" fontWeight={700}>{p.clicks ?? 0}</Typography></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>

                                {/* Mobile: stacked cards */}
                                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                    {perProduct.map((p) => (
                                        <Box key={p.product_id} sx={{ p: 2, borderBottom: '1px solid #F1F5F9', '&:last-of-type': { borderBottom: 0 } }}>
                                            <Typography variant="body2" fontWeight={600} noWrap title={p.product_title}>{p.product_title}</Typography>
                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.5 }} title={displayLink(p)}>{displayLink(p)}</Typography>
                                            <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>{p.clicks ?? 0} clicks</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}
                    </Paper>
                </>
            )}
        </Box>
    );
};
